<script setup lang="ts">
import Notification from '@/Components/Utils/Notification.vue'
import IrisHeader from '@/Layouts/Iris/Header.vue'
import { set } from 'lodash-es'
import "@/../css/iris_styling.css"
import Footer from '@/Layouts/Iris/Footer.vue'
import { useColorTheme } from '@/Composables/useStockList'
import { Head, usePage } from '@inertiajs/vue3'
import { provide, ref, onMounted, onBeforeUnmount, watch, computed, defineAsyncComponent } from 'vue'
import { initialiseIrisApp } from '@/Composables/initialiseIris'
import { useIrisLayoutStore } from "@/Stores/irisLayout"
import { ctrans } from '@/Composables/useTrans'
const page = usePage()
const ScreenWarning = defineAsyncComponent(() => import('@/Components/Utils/ScreenWarning.vue'))
const Modal = defineAsyncComponent(() => import('@/Components/Utils/Modal.vue'))
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { faHome } from "@fal/faHome";import { faImage } from "@fal/faImage";import { faSparkles } from "@fal/faSparkles";import { faSignIn } from "@fal/faSignIn";import { faPlusCircle } from "@fal/faPlusCircle";import { faGift } from "@fal/faGift";import { faMedal } from "@fal/faMedal";import { faSkull } from "@fal/faSkull";import { faSkullCow } from "@fal/faSkullCow";import { faSkullCrossbones } from "@fal/faSkullCrossbones";import { faCheck } from "@fal/faCheck";import { faTimes } from "@fal/faTimes";import { faLock } from "@fal/faLock";
import { faMedal as fasMedal } from "@fas/faMedal";import { faCandleHolder } from "@fas/faCandleHolder";import { faCircle } from "@fas/faCircle";import { faBoxFull } from "@fas/faBoxFull";import { faExclamationTriangle } from "@fas/faExclamationTriangle";
import { faMedal as fadMedal } from "@fad/faMedal";
import { library } from '@fortawesome/fontawesome-svg-core'
import Button from '@/Components/Elements/Buttons/Button.vue'
import { irisStyleVariables } from '@/Composables/Workshop'
import { initialiseIrisVarnish } from '@/Composables/initialiseIrisVarnish'
import { whenIrisLoggedIn } from '@/Composables/irisAuthFlag'
import { recordWebVitals } from '@/Composables/recordWebVitals'
import { setColorStyleRoot } from '@/Composables/useApp'
import { getStyles } from '@/Composables/styles'
import BreadcrumbsIris from '@/Components/Navigation/BreadcrumbsIris.vue'
import IrisAnnouncement from './Iris/IrisAnnouncement.vue'
import IrisWebsiteDialog from './Iris/IrisWebsiteDialog.vue'
import { announcementsAtPosition, useAnnouncementClock, type AnnouncementAudience } from '@/Iris/Composables/useAnnouncementVisibility'
import axios from 'axios'
const BundleSidebar = defineAsyncComponent(() => import('@/Components/Dropshipping/BundleSidebar.vue'))
import { useBundle } from '@/Composables/useBundle'
import { createSidePanel } from '@/Iris/Composables/useSidePanel'
import SidePanel from '@/Iris/Components/SidePanel.vue'

interface ChatConfig {
    is_online: boolean
    schedule?: {
        start: string
        end: string
        timezone: string
    }
}

const bundle = useBundle()
library.add(faBoxFull, faHome, faImage, faSparkles, faSignIn, faPlusCircle, faGift, faCandleHolder, faExclamationTriangle, faMedal, fasMedal, faCircle, fadMedal, faWhatsapp, faSkull, faSkullCow, faSkullCrossbones, faCheck, faTimes, faLock)

initialiseIrisApp()

const layout = useIrisLayoutStore()
const isOpenMenuMobile = ref(false)
provide('layout', layout)
provide('isOpenMenuMobile', isOpenMenuMobile)
const BUNDLE_PANEL_WIDTH = 320
const bundleToggleStyle = computed(() => {
    const gap = 16
    const openOffset = BUNDLE_PANEL_WIDTH + gap
    const right = bundle.open.value ? openOffset : gap
    return {
        top: '60%',
        right: `${right}px`,
        color: layout.app.theme[1]
    }
})

const announcementNow = useAnnouncementClock()

const announcementAtPosition = (position: string) =>
    computed(() => announcementsAtPosition((usePage().props?.announcements ?? []) as any[], position, announcementNow.value))

const announcementAudienceClass: Record<AnnouncementAudience, string> = {
    everyone: '',
    logged_in: 'hidden [.iris-logged-in_&]:block',
    logged_out: '[.iris-logged-in_&]:hidden',
}

const propsAnnouncementsTopbar = announcementAtPosition('top-bar')
const propsAnnouncementsBottomMenu = announcementAtPosition('bottom-menu')
const propsAnnouncementsTopFooter = announcementAtPosition('top-footer')
const header = usePage().props?.iris?.header
const navigation = usePage().props?.iris?.menu
const theme = usePage().props?.iris?.theme ? usePage().props?.iris?.theme : { color: [...useColorTheme[2]] }
const screenType = ref<'mobile' | 'tablet' | 'desktop'>('desktop')
const customSidebar = usePage().props?.iris?.sidebar
const useChat = usePage().props?.use_chat
const chatConfig = usePage().props?.chat_config as ChatConfig

const fallbackTheme = theme

const safeTheme = computed(() => {
    const t = layout?.app?.theme

    return (t && t.length >= 8) ? t : fallbackTheme
})


const isFirstVisit = () => {
    if (typeof window !== "undefined") {
        const irisData = localStorage.getItem('iris');
        if (irisData) {
            const parsedData = JSON.parse(irisData);
            return parsedData.isFirstVisit;
        }
    }
    return true;
};

const firstVisit = ref(isFirstVisit());

const setFirstVisitToFalse = () => {
    if (typeof window !== "undefined") {
        const irisData = localStorage.getItem('iris');
        if (irisData) {
            const parsedData = JSON.parse(irisData);
            parsedData.isFirstVisit = false;
            localStorage.setItem('iris', JSON.stringify(parsedData));
        } else {
            localStorage.setItem('iris', JSON.stringify({ isFirstVisit: false }));
        }
    }
    firstVisit.value = false
};

const checkScreenType = () => {
    const width = window.innerWidth
    if (width < 640) screenType.value = 'mobile'
    else if (width >= 640 && width < 1024) screenType.value = 'tablet'
    else screenType.value = 'desktop'
}


provide('screenType', screenType)


const handleTabFocus = () => {
    if (document.visibilityState === 'visible') {
        checkScreenType()
        if (layout?.iris?.is_logged_in) {
            fetchHasInBasket()
        }
    }
}

const resolvePaddingProp = (padding: any, screen: string, side?: string) => {
    const read = (obj: any) => {
        if (!obj || typeof obj !== 'object') return undefined
        return side ? obj?.[side]?.value : obj?.unit
    }
    const fromScreen = read(padding?.[screen])
    if (fromScreen !== undefined) return fromScreen
    if (screen !== 'desktop') {
        const fromDesktop = read(padding?.desktop)
        if (fromDesktop !== undefined) return fromDesktop
    }
    return read(padding)
}

const containerPaddingCss = (() => {
    const padding = theme?.container?.properties?.padding
    if (!padding || typeof padding !== 'object') return null
    if (padding.__irisCssVars) {
        return padding.__irisCssVars
    }

    const sides: [string, string][] = [['left', '--iris-cp-l'], ['right', '--iris-cp-r']]
    const rulesByScreen: Record<string, string> = {}

    for (const screen of ['mobile', 'tablet', 'desktop']) {
        const rules: string[] = []
        for (const [side, cssVar] of sides) {
            const value = resolvePaddingProp(padding, screen, side)
            const unit = resolvePaddingProp(padding, screen)
            if (value === null || value === undefined || !unit || (typeof value === 'string' && value.startsWith('var('))) {
                return null
            }
            rules.push(`${cssVar}:${value}${unit}`)
        }
        rulesByScreen[screen] = rules.join(';')
    }

    for (const [side, cssVar] of sides) {
        for (const target of [padding, padding.mobile, padding.tablet, padding.desktop]) {
            if (target?.[side]) {
                target[side].value = `var(${cssVar})`
            }
        }
    }

    padding.__irisCssVars = `:root{${rulesByScreen.mobile}}@media(min-width:640px){:root{${rulesByScreen.tablet}}}@media(min-width:1024px){:root{${rulesByScreen.desktop}}}`

    return padding.__irisCssVars
})()

layout.app.webpage_layout = theme

onMounted(() => {
    initialiseIrisVarnish(useIrisLayoutStore)
    recordWebVitals((usePage().props?.webpage_id as number | undefined) ?? null)
    checkScreenType()
    setColorStyleRoot(theme?.color)
    window.addEventListener('resize', checkScreenType)

    document.addEventListener('visibilitychange', handleTabFocus)

    irisStyleVariables(theme?.color)

    whenIrisLoggedIn(layout, fetchHasInBasket)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', checkScreenType)
    document.removeEventListener('visibilitychange', handleTabFocus)
})


const fetchHasInBasket = async () => {
    set(layout, ['family_page', 'productInBasket', 'isLoading'], true)
    try {
        const apiUrl = `/json/basket/transaction-data`

        if (!apiUrl) {
            throw new Error("Invalid model_type or missing route configuration");
        }

        const response = await axios.get(apiUrl);
        set(layout, ['family_page', 'productInBasket', 'list'], response.data || {})
    } catch (error) {
        console.error('Failed to load product portfolio', error);
    } finally {
        set(layout, ['family_page', 'productInBasket', 'isLoading'], false)

    }
};

// Watch: open Side Basket if cart has any changes
watch(() => layout.iris_variables?.cart_amount, (newVal) => {
    if (typeof layout.rightbasket?.show === 'undefined') {
        set(layout, 'rightbasket.show', true)
        set(layout, 'rightbasket.tab', 'basket')
    }
})

createSidePanel(layout, screenType, !!useChat)

const syncLoggedInClass = () => document.documentElement.classList.toggle('iris-logged-in', !!layout.iris?.is_logged_in)

onMounted(syncLoggedInClass)

watch(() => layout.iris?.is_logged_in, syncLoggedInClass)

</script>

<template>
    <div class="editor-class">
        <Head v-if="page.component !== 'IrisWebpage'">
            <link rel="canonical" head-key="canonical" :href="page.props.canonical_url" />
        </Head>
        <component :is="'style'" v-if="containerPaddingCss">{{ containerPaddingCss }}</component>

        <ScreenWarning v-if="layout.app.environment === 'staging'">
            {{ ctrans("This environment is for testing and development purposes only. The data you enter will be deleted in the future.") }}
        </ScreenWarning>

        <Modal v-if="layout.app.environment === 'staging'" :isOpen="firstVisit"
            :diazxclogStyle="{ background: '#fff', border: '0px solid #ff0000' }" width="w-fit">
            <div class="px-6 py-28 sm:px-6 lg:px-32 text-red-600">
                <div class="mx-auto max-w-2xl text-center">
                    <h2 class="text-4xl font-bold tracking-tight text-balance sm:text-5xl">
                        <FontAwesomeIcon :icon="faExclamationTriangle" class='text-4xl' fixed-width
                            aria-hidden='true' />
                        Reminder
                        <FontAwesomeIcon :icon="faExclamationTriangle" class='text-4xl' fixed-width
                            aria-hidden='true' />
                    </h2>
                    <p class="mx-auto mt-6 text-lg/8 text-pretty">Warning: You are currently in the staging environment.
                        Data can be delayed and overwritten at any time and may be deleted in the future.</p>

                    <div class="mt-10 flex items-center justify-center gap-x-6">
                        <Button @click="setFirstVisitToFalse" size="xl" label="Got it" type="red">

                        </Button>
                    </div>
                </div>
            </div>
        </Modal>

        <!-- Modal: is blocked -->
        <Modal
            v-if="layout.is_blocked"
            :isOpen="layout.is_blocked"
            :isClosableInBackground="false"
            :zIndex="9999"
            width="w-full max-w-lg"
        >
            <div class="px-6 py-12 text-center">
                <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                    <FontAwesomeIcon :icon="faLock" class="text-3xl text-red-600" fixed-width aria-hidden="true" />
                </div>
                <h2 class="mt-6 text-2xl font-bold tracking-tight text-gray-900">
                    {{ ctrans("Access Unauthorized") }}
                </h2>
                <p class="mx-auto mt-4 text-base text-gray-600">
                    {{ ctrans("You are not authorised to continue. Please contact support for assistance.") }}
                </p>
            </div>
        </Modal>

        <div :class="[(theme.layout === 'blog' || !theme.layout) ? 'container max-w-7xl mx-auto shadow-xl' : '']">
            <IrisAnnouncement
                v-for="item in propsAnnouncementsTopbar"
                :key="item.announcement.ulid"
                :class="announcementAudienceClass[item.audience]"
                :data="item.announcement"
            />

            <!-- Section: Topbar, Header, Menu, Sidebar -->
            <IrisHeader
                v-if="header?.header"
                :data="header"
                :colorThemed="theme"
                :menu="navigation"
                :screen-type="screenType"
                :custom-sidebar="customSidebar"
            />

            <IrisAnnouncement
                v-for="item in propsAnnouncementsBottomMenu"
                :key="item.announcement.ulid"
                :class="announcementAudienceClass[item.audience]"
                :data="item.announcement"
            />

            <div class="border-b border-gray-200 ">
                <div
                    class="transition-all border-b-0 border-transparent"
                    :style="{
                        ...getStyles(layout?.app?.webpage_layout?.container?.properties, screenType),
                        marginLeft: getStyles(layout?.app?.webpage_layout?.container?.properties, screenType)?.paddingLeft,
                        marginRight: getStyles(layout?.app?.webpage_layout?.container?.properties, screenType)?.paddingRight,
                        paddingLeft: '0',
                        paddingRight: '0',
                        paddingTop: '0',
                        paddingBottom: '0',
                    }"
                >
                    <BreadcrumbsIris
                        v-if="usePage().props.breadcrumbs?.length"
                        id="iris_breadcrumbs"
                        :breadcrumbs="usePage().props.breadcrumbs ?? []"
                        :navigation="usePage().props.navigation ?? []"
                        :layout="layout"
                    />
                </div>
            </div>

            <main class="relative flex">
                <!-- Layout: main page -->
                <div class="flex-1 min-w-0">
                    <slot />
                </div>

                <div
                    v-if="bundle.open.value"
                    :class="bundle.open.value
                        ? 'w-[350px] border-l-gray-300 sticky z-[52] border-l top-0 pointer-events-auto h-screen transition-all'
                        : 'w-0 border-transparent'"
                    >
                    <BundleSidebar :symbol="layout.iris.currency?.symbol" :code="layout.iris.currency?.code"/>
                </div>

                <div
                    v-if="layout?.iris?.is_logged_in && screenType !== 'mobile' && layout.app?.name === 'iris' && layout.retina?.type === 'dropshipping'"
                    @click="bundle.open.value = !bundle.open.value"
                    class="fixed z-[60] w-8 aspect-square rounded-full flex items-center justify-center cursor-pointer
                        bg-[var(--theme-color-0)]
                        hover:bg-[color-mix(in_srgb,var(--theme-color-0)75%,black)]"
                    :style="bundleToggleStyle"
                    v-tooltip="'Toggle Bundle'"
                >
                <FontAwesomeIcon :icon="faBoxFull" class='text-md' fixed-width
                            aria-hidden='true' />
                </div>
            </main>
            <IrisAnnouncement
                v-for="item in propsAnnouncementsTopFooter"
                :key="item.announcement.ulid"
                :class="announcementAudienceClass[item.audience]"
                :data="item.announcement"
            />

            <Footer :colorThemed="theme" />
        </div>

        <IrisWebsiteDialog />

    </div>

    <notifications dangerously-set-inner-html :max="3" width="500" classes="custom-style-notification"
        :pauseOnHover="true">
        <template #body="props">
            <Notification :notification="props" />
        </template>
    </notifications>


    <SidePanel :isChatEnabled="!!useChat" :chatConfig="chatConfig" />
</template>

<style lang="scss">

html {
  overscroll-behavior: none;
}

/* * {
  outline: 1px solid red;
} */


#iris_breadcrumbs ol,
#iris_breadcrumbs ul {
    margin-left: 0;
    margin-top: 0;
    list-style-position: outside;
}

#iris_breadcrumbs ol li,
#iris_breadcrumbs ul li {
    margin-left: 0;
    margin-top: 0;
    padding-left: 0;
    padding-top: 0;
}

.vue-notification-group {
    width: 300px !important;

    @media (min-width: 640px) {
        width: 500px !important;
    }
}


// INI-562: live chat
iframe#launcher {
    bottom: 30px !important;
}

.background-primary {
    background-color: var(--theme-color-4);
}

.border-primary {
    border-color: var(--theme-color-4);
}

.text-primary {
    color: var(--theme-color-4) !important;
}

.primaryLink {
    background: v-bind('`linear-gradient(to top, #fcd34d, #fcd34d)`');

    &:hover,
    &:focus {
        color: #374151;
    }

    @apply focus:ring-0 focus:outline-none focus:border-none bg-no-repeat [background-position:0%_100%] [background-size:100%_0.2em] motion-safe:transition-all motion-safe:duration-200 hover:[background-size:100%_100%] focus:[background-size:100%_100%] px-1 py-0.5
}

.secondaryLink {
    background: v-bind('`linear-gradient(to top, ${safeTheme[6]}, ${safeTheme[6] + "AA"})`');

    &:hover,
    &:focus {
        color: v-bind('`${safeTheme[7]}`');
    }

    @apply focus:ring-0 focus:outline-none focus:border-none bg-no-repeat [background-position:0%_100%] [background-size:100%_0.2em] motion-safe:transition-all motion-safe:duration-200 hover:[background-size:100%_100%] focus:[background-size:100%_100%] px-1 py-0.5
}


</style>
